import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Lock, Plus, RefreshCw, ShieldCheck } from 'lucide-react';
import { AppLayout } from '@/components/layout/app-layout';
import { Aviso } from '@/components/ui/aviso';
import { Button } from '@/components/ui/button';
import { EstadoError, EstadoVacio } from '@/components/ui/estado-vacio';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils/cn';
import { Tarjeta } from '@/features/compite/ui/tarjeta';
import { Pildoras } from '@/features/compite/ui/pildoras';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { HistorialDeCambios } from '@/features/smart-pricing/components/historial-de-cambios';
import { historialDeReglaEnPalabras } from '../lib/historial-de-regla-en-palabras';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { useToast } from '@/lib/hooks/use-toast';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { formatoCantidad } from '@/lib/utils/formato-precio';
import { ponerGuardiaDeSalida } from '@/lib/salida-con-cambios';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { pricingRulesApi } from '../services/pricing-rules-api';
import { enLista } from '../lib/precio-escrito';
import { usePermisosDeReglas } from '../lib/use-permisos-de-reglas';
import type {
  BorradorInicial,
  ConteosDeReglas,
  EstadoDelRecalculo,
  EstadoSimulado,
  PricingRule,
} from '../types/pricing-rules.types';
import { AyudaReglas } from '../components/ayuda-reglas';
import {
  ListaDeReglas,
  filtrarPorAlcance,
  type FiltroAlcance,
} from '../components/lista-de-reglas';
import { NuevaReglaDialog } from '../components/nueva-regla-dialog';
import { avisoDeRegla, estadoDeRegla } from '../lib/reglas-en-palabras';
import { ComoEstaCadaProducto } from '../components/como-esta-cada-producto';
import { RecalcularDialog } from '../components/recalcular-dialog';
import { AvisoReglasSinRecalcular } from '../components/aviso-reglas-sin-recalcular';
import type { ReglasSinRecalcular } from '../types/pricing-rules.types';
import { BOTON_DEGRADADO } from '@/features/compite/ui/boton-degradado';
import { atributosSoloMira, useSoporteSoloMira } from '@/features/mini-prixia/soporte-solo-mira';

const FILTROS: Array<{ clave: FiltroAlcance; texto: string }> = [
  { clave: 'TODAS', texto: 'Todas' },
  { clave: 'CATALOGO', texto: 'Todo el catálogo' },
  { clave: 'CATEGORIA', texto: 'Por categoría' },
  { clave: 'MARCA', texto: 'Por marca' },
];

/** `?ver=sin-costo` (la tarjeta de Hoy) abre «Cómo está cada producto» filtrado. */
function vistaInicial(): {
  pestana: 'reglas' | 'productos';
  filtro: { sinCosto?: boolean; estado?: EstadoSimulado } | null;
} {
  try {
    const ver = new URLSearchParams(window.location.search).get('ver');
    if (ver === 'sin-costo') return { pestana: 'productos', filtro: { sinCosto: true } };
    if (ver === 'productos') return { pestana: 'productos', filtro: null };
  } catch {
    /* sin window: la de siempre */
  }
  return { pestana: 'reglas', filtro: null };
}

/**
 * La pantalla de Reglas (maqueta S1). `embebida` la usa el plan 3 (Tarea 1)
 * para montarla en su destino Reglas: sin `h1`, sin `AppLayout` y SIN la
 * pestaña «Cómo está cada producto», que allá es el destino Productos (ruling
 * del 25-sep): si no, habría dos «Cómo está cada producto» y dos «Reglas».
 * Qué botones ve cada uno lo decide la página sola, suelta o embebida.
 */
export default function ReglasPage({
  embebida = false,
  onIrAPorDecidirDelRecalculo,
  onCambiosSinAplicar,
  onTotal,
  enCompite = false,
  destinoDeLaAccion = null,
}: {
  embebida?: boolean;
  /**
   * Compite › Estrategia, como la maqueta: la ayuda es una línea arriba de todo
   * («ⓘ Las reglas son límites… ¿Cómo funcionan?») y «Nueva regla» va al
   * encabezado de la página (`destinoDeLaAccion`), no dentro de la tarjeta.
   */
  enCompite?: boolean;
  /** Dónde va el botón principal en Compite (el lado derecho del encabezado). */
  destinoDeLaAccion?: HTMLElement | null;
  /**
   * Lote C1: «Ir a Por decidir» después de recalcular, ya filtrado por «Origen:
   * recálculo». Embebida lo pasa el destino; suelta, va por enlace.
   */
  onIrAPorDecidirDelRecalculo?: () => void;
  /** Spec 294 (D-R4-4): cuántos cambios sin aplicar hay (la usa la Tarea 11, al salir). */
  onCambiosSinAplicar?: (n: number) => void;
  /**
   * Compite › Estrategia (rediseño del 14-oct): cuántas reglas hay, para el
   * contador de la pestaña. Se avisa al cargar; quien la pasa la memoriza.
   */
  onTotal?: (n: number) => void;
}) {
  const { toast } = useToast();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  // Un permiso por acción (decisión del 26-sep, 4B-datos T1b): cada botón sale
  // sólo con el suyo. El backend responde 403 igual: la pantalla no ofrece lo que
  // después fallaría. Entrar (ver) lo cuida la ruta, o la pestaña del plan 3.
  const permisos = usePermisosDeReglas();
  const soloLectura = !permisos.cambiaAlgo;
  // Soporte («Entrar como») en una demo sólo mira: nada de crear, aplicar ni descartar.
  const sinCambios = atributosSoloMira(useSoporteSoloMira());
  // Spec 294 (orquestador, 7-oct): «Descartar este cambio» con sólo editar vale
  // para un cambio que escribió esa persona sola; el servidor mira a la persona
  // REAL. En modo soporte la pantalla sólo conoce a la persona vista: por esa vía
  // no se ofrece (con encender, sí).
  const { user, isImpersonating } = useAuth();
  const yo = isImpersonating ? null : (user?.id ?? null);
  // Embebida no hay pestaña de productos: `?ver=` lo atiende el destino Productos.
  const inicial = useMemo(
    () => (embebida ? { pestana: 'reglas' as const, filtro: null } : vistaInicial()),
    [embebida]
  );

  const [reglas, setReglas] = useState<PricingRule[]>([]);
  const [conteos, setConteos] = useState<ConteosDeReglas | null>(null);
  const [cargando, setCargando] = useState(true);
  useEffect(() => {
    if (!cargando) onTotal?.(reglas.length);
  }, [cargando, reglas.length, onTotal]);
  const [error, setError] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<FiltroAlcance>('TODAS');
  const [pestana, setPestana] = useState(inicial.pestana);
  const [editando, setEditando] = useState<PricingRule | null>(null);
  // Spec 294 (D-R4-5): la regla cuyo historial se está mirando.
  const [historialDe, setHistorialDe] = useState<PricingRule | null>(null);
  const [borradorInicial, setBorradorInicial] = useState<BorradorInicial | null>(null);
  const [abierto, setAbierto] = useState(false);
  // Sube cuando termina un recálculo: «Cómo está cada producto» se vuelve a montar
  // y lee la foto. Spec 294: guardar ya no la cambia (la foto es de lo vigente).
  const [versionFoto, setVersionFoto] = useState(0);
  // Lote C1 (punto 8, D11): el recálculo y el aviso de reglas cambiadas sin recalcular.
  const [recalculando, setRecalculando] = useState(false);
  const [sinRecalcular, setSinRecalcular] = useState<ReglasSinRecalcular | null>(null);
  // Spec 294 (D-R4-4): los cambios sin aplicar (todavía no rigen).
  const [cambios, setCambios] = useState<EstadoDelRecalculo['cambiosSinAplicar']>(null);
  // Ronda final de R4 (revisión de la T10): cada lectura toma un número; una
  // respuesta vieja no pisa a la nueva. Y una falla pasajera (un timeout) deja el
  // aviso como estaba: borrarlo diría que no hay cambios sin aplicar.
  const pedidoDelAviso = useRef(0);
  const leerAviso = useCallback(() => {
    const n = ++pedidoDelAviso.current;
    Promise.resolve()
      .then(() => pricingRulesApi.estadoDelRecalculo())
      .then((e) => {
        if (n !== pedidoDelAviso.current) return;
        setSinRecalcular(e.reglasSinRecalcular);
        setCambios(e.cambiosSinAplicar ?? null);
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    leerAviso();
  }, [leerAviso]);
  const cuantosCambios = cambios?.cuantos ?? 0;
  useEffect(() => {
    onCambiosSinAplicar?.(cuantosCambios);
  }, [cuantosCambios, onCambiosSinAplicar]);

  /** `silencioso`: después de un cambio, sin el esqueleto (la lista no parpadea). */
  const pedidoDeLaLista = useRef(0);
  const cargar = useCallback(async (silencioso = false) => {
    const n = ++pedidoDeLaLista.current;
    if (!silencioso) {
      setCargando(true);
      setError(null);
    }
    try {
      const lista = await pricingRulesApi.list();
      // Ronda final de R4: una lectura vieja que vuelve tarde no pisa a la nueva.
      if (n === pedidoDeLaLista.current) setReglas(lista);
    } catch (e) {
      // Una relectura que falla deja la lista como estaba (el cambio ya se guardó).
      if (!silencioso)
        setError(mensajeDeError(e, 'No se pudieron cargar las reglas. Probá de nuevo.'));
    } finally {
      if (!silencioso) setCargando(false);
    }
    // Los conteos son una ayuda: si fallan, la lista se ve igual, con «—».
    pricingRulesApi
      .conteos(7)
      .then(setConteos)
      .catch(() => setConteos(null));
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // Ronda de la T11 (I3, decisión del orquestador): las reglas que esta persona
  // tocó en ESTA visita (esta pestaña). La guardia al salir y el descarte al
  // cerrar cubren sólo éstas; los cambios propios que ya estaban (otra pestaña,
  // la ficha del producto, una visita anterior) van al aviso «de antes», sin
  // descarte automático.
  const [tocadas, setTocadas] = useState<ReadonlySet<string>>(() => new Set());

  // Spec 294 (D-R4-4): después de cada cambio, la fila (con su cambio sin aplicar)
  // y el aviso se vuelven a pedir. `ruleId`: la regla que se tocó (no al descartar).
  const despuesDeCambiar = (ruleId?: string) => {
    if (ruleId) setTocadas((t) => (t.has(ruleId) ? t : new Set(t).add(ruleId)));
    void cargar(true);
    leerAviso();
  };

  // Qué cambios escribió esta persona sola y en cuántos más metió mano junto con
  // otra (de GET /borradores, el mismo criterio que `esPropio` del servidor). Sin
  // encender, con editar puede descartar los suyos, y con borrar puede borrar una
  // nueva suya sin aplicar (borrarla la descarta: de otra persona pide encender).
  // Y salir de Reglas con cambios suyos pregunta antes (Tarea 11).
  const [soloMios, setSoloMios] = useState<Set<string>>(() => new Set());
  const [compartidos, setCompartidos] = useState<Set<string>>(() => new Set());
  const [escribieronDe, setEscribieronDe] = useState<Map<string, string[]>>(() => new Map());
  const hayCambiosEnLaLista = reglas.some((r) => r.borrador);
  useEffect(() => {
    if (cargando) return;
    if (!hayCambiosEnLaLista) {
      setSoloMios(new Set());
      setCompartidos(new Set());
      setEscribieronDe(new Map());
      return;
    }
    let vigente = true;
    // Es una ayuda: si falla, no se ofrece (el servidor lo negaría igual).
    Promise.resolve()
      .then(() => pricingRulesApi.cambiosSinAplicar())
      .then(({ cambios: cs }) => {
        if (!vigente) return;
        setEscribieronDe(new Map(cs.map((c) => [c.ruleId, c.escribieron ?? []])));
        if (yo == null) {
          setSoloMios(new Set());
          setCompartidos(new Set());
          return;
        }
        const mios = new Set(
          cs
            .filter((c) => c.escribieronIds.length === 1 && c.escribieronIds[0] === yo)
            .map((c) => c.ruleId)
        );
        setSoloMios(mios);
        setCompartidos(
          new Set(
            cs
              .filter((c) => c.escribieronIds.length > 1 && c.escribieronIds.includes(yo))
              .map((c) => c.ruleId)
          )
        );
      })
      .catch(() => {
        if (!vigente) return;
        setSoloMios(new Set());
        setCompartidos(new Set());
        setEscribieronDe(new Map());
      });
    return () => {
      vigente = false;
    };
  }, [reglas, cargando, hayCambiosEnLaLista, yo]);
  const puedeDescartarElCambio = (r: PricingRule) =>
    permisos.encender || (permisos.editar && soloMios.has(r.id));
  // Borrar una nueva sin aplicar la descarta, y borrar una que tiene un cambio sin
  // aplicar reemplaza ese cambio (ronda final de R4): si el cambio no es sólo de
  // esta persona, el servidor pide encender. No se ofrece lo que daría 403.
  const puedeBorrar = (r: PricingRule) => !r.borrador || permisos.encender || soloMios.has(r.id);

  // Spec 294 (decisión del dueño, 7-oct): con cambios propios sin aplicar, salir de
  // Reglas (otra pestaña, el menú lateral, un enlace, la flecha atrás, cambiar de
  // empresa, cerrar la ventana) avisa que se descartan. «Descartar y salir»
  // descarta SÓLO los de esta persona (con cualquier permiso de cambiar reglas);
  // los que también cambió otra quedan sin aplicar. Ronda de la T11 (I3): sólo
  // los de las reglas tocadas en esta visita.
  const misTocadas = useMemo(
    () => [...tocadas].filter((id) => soloMios.has(id)),
    [tocadas, soloMios]
  );
  const compartidasTocadas = useMemo(
    () => [...tocadas].filter((id) => compartidos.has(id)).length,
    [tocadas, compartidos]
  );
  // Los propios que no se tocaron en esta visita: «de antes».
  const deAntes = useMemo(
    () => [...soloMios].filter((id) => !tocadas.has(id)),
    [soloMios, tocadas]
  );
  // La guardia se vuelve a poner sólo si cambian las reglas (no en cada lectura).
  const clavesDeMisTocadas = misTocadas.join(',');
  useEffect(() => {
    if (clavesDeMisTocadas === '') return;
    const reglasADescartar = clavesDeMisTocadas.split(',');
    const n = reglasADescartar.length;
    return ponerGuardiaDeSalida({
      preguntar: (salir) =>
        confirm({
          title: `Tenés ${n === 1 ? '1 cambio' : `${formatoCantidad(n)} cambios`} en reglas sin aplicar.`,
          description:
            'Si salís, se descartan: no van a regir. Para que rijan, usá «Aplicar las reglas y recalcular».' +
            (compartidasTocadas === 0
              ? ''
              : compartidasTocadas === 1
                ? ' 1 regla que también cambió otra persona queda sin aplicar.'
                : ` ${formatoCantidad(compartidasTocadas)} reglas que también cambió otra persona quedan sin aplicar.`),
          confirmText: 'Descartar y salir',
          cancelText: 'Quedarme',
          variant: 'destructive',
          onConfirm: async () => {
            try {
              await pricingRulesApi.descartarMios(reglasADescartar);
            } catch (e) {
              toast({
                title: 'No se pudieron descartar tus cambios',
                description: mensajeDeError(e, 'Probá de nuevo.'),
                variant: 'destructive',
              });
              return;
            }
            salir();
          },
        }),
      alCerrar: () => pricingRulesApi.descartarMiosAlCerrar(reglasADescartar),
    });
  }, [clavesDeMisTocadas, compartidasTocadas, confirm, toast]);

  /** «Descartarlos» del aviso «de antes»: sólo esos (no lo tocado en esta visita). */
  const onDescartarLosDeAntes = async () => {
    try {
      const { descartados } = await pricingRulesApi.descartarMios(deAntes);
      toast({
        title:
          descartados === 1
            ? 'Se descartó 1 cambio'
            : `Se descartaron ${formatoCantidad(descartados)} cambios`,
      });
      despuesDeCambiar();
    } catch (e) {
      toast({
        title: 'No se pudieron descartar tus cambios',
        description: mensajeDeError(e, 'Probá de nuevo.'),
        variant: 'destructive',
      });
    }
  };

  // Spec 294 (D-R4-2 y decisión del dueño del 7-oct): apagar y encender piden
  // confirmación; ninguno rige hasta aplicar. Si el único cambio sin aplicar era el
  // encendido, volver a tocarlo lo deja como rige hoy (no queda cambio).
  const onToggle = (r: PricingRule) => {
    const apagar = r.isActive;
    // Ronda final de R4 (revisión de la T10): «sigue rigiendo» sólo si hoy rige
    // encendida. Una NUEVA sin aplicar, o una que hoy rige apagada, todavía no rige.
    const rigeEncendida =
      r.vigente !== undefined ? r.vigente?.isActive === true : r.borrador?.tipo !== 'NUEVA';
    const vuelve =
      r.borrador?.tipo === 'CAMBIO' &&
      r.borrador.campos.length === 1 &&
      r.borrador.campos[0] === 'isActive';
    confirm({
      title: apagar ? `¿Apagar «${r.name}»?` : `¿Encender «${r.name}»?`,
      description: vuelve
        ? 'Vuelve a quedar como rige hoy: no queda ningún cambio sin aplicar.'
        : apagar
          ? rigeEncendida
            ? 'Queda como cambio sin aplicar: sigue rigiendo hasta que apliques las reglas y recalcules.'
            : 'Todavía no rige: queda apagada al aplicar.'
          : 'Se enciende al aplicar: hasta que apliques las reglas y recalcules, no rige.',
      confirmText: apagar ? 'Sí, apagar' : 'Aceptar',
      onConfirm: async () => {
        try {
          // El servidor invierte: se usa lo que devuelve, no lo que la pantalla cree.
          const actualizada = await pricingRulesApi.toggle(r.id);
          setReglas((rs) => rs.map((x) => (x.id === r.id ? { ...x, ...actualizada } : x)));
          toast({
            title:
              actualizada.borrador === null
                ? `«${r.name}» queda como rige hoy`
                : actualizada.isActive
                  ? `«${r.name}» se enciende al aplicar`
                  : rigeEncendida
                    ? `«${r.name}» se apaga al aplicar`
                    : `«${r.name}» queda apagada`,
          });
          despuesDeCambiar(r.id);
        } catch (e) {
          toast({
            title: 'No se pudo cambiar la regla',
            description: mensajeDeError(e, 'Probá de nuevo.'),
            variant: 'destructive',
          });
        }
      },
    });
  };

  const onBorrar = (r: PricingRule) => {
    const nuevaSinAplicar = r.borrador?.tipo === 'NUEVA';
    // Ronda final de R4: borrar una que tiene un cambio sin aplicar lo reemplaza.
    const quienes = escribieronDe.get(r.id) ?? [];
    const reemplaza =
      !nuevaSinAplicar && r.borrador
        ? ` El cambio sin aplicar de esta regla${quienes.length > 0 ? ` (de ${enLista(quienes)})` : ''} se descarta.`
        : '';
    const rigeEncendida = r.vigente !== undefined ? r.vigente?.isActive === true : r.isActive;
    confirm({
      title: `¿Borrar «${r.name}»?`,
      description: nuevaSinAplicar
        ? 'Todavía no rige: se descarta.'
        : (rigeEncendida
            ? 'Se borra al aplicar las reglas y recalcular; hasta entonces sigue rigiendo.'
            : 'Se borra al aplicar las reglas y recalcular.') + reemplaza,
      confirmText: 'Sí, borrar',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          const r2 = await pricingRulesApi.delete(r.id);
          const seBorraAlAplicar = r2?.seBorraAlAplicar ?? !nuevaSinAplicar;
          toast({
            title: seBorraAlAplicar ? `«${r.name}» se borra al aplicar` : `Se descartó «${r.name}»`,
          });
          despuesDeCambiar(r.id);
        } catch (e) {
          toast({
            title: 'No se pudo borrar',
            description: mensajeDeError(e, 'Probá de nuevo.'),
            variant: 'destructive',
          });
        }
      },
    });
  };

  /** Spec 294 (decisión del dueño, 7-oct): descartar sólo el cambio de esta regla. */
  const onDescartarCambio = (r: PricingRule) =>
    confirm({
      title: `¿Descartar el cambio sin aplicar de «${r.name}»?`,
      description:
        r.borrador?.tipo === 'NUEVA'
          ? 'Todavía no rige: se descarta. No se puede deshacer.'
          : 'La regla queda como rige hoy. No se puede deshacer.',
      confirmText: 'Sí, descartar',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          await pricingRulesApi.descartarCambio(r.id);
          toast({ title: `Se descartó el cambio de «${r.name}»` });
          despuesDeCambiar();
        } catch (e) {
          toast({
            title: 'No se pudo descartar el cambio',
            description: mensajeDeError(e, 'Probá de nuevo.'),
            variant: 'destructive',
          });
        }
      },
    });

  /** Spec 294 (D-R4-4): «Descartar todos los cambios» (los de todos; pide encender). */
  const onDescartarTodos = () => {
    const n = cambios?.cuantos ?? 0;
    confirm({
      title: `¿Descartar ${n === 1 ? '1 cambio' : `${formatoCantidad(n)} cambios`} sin aplicar?`,
      description: 'Las reglas quedan como rigen hoy. No se puede deshacer.',
      confirmText: 'Sí, descartar',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          const { descartados } = await pricingRulesApi.descartarCambios();
          toast({
            title:
              descartados === 1
                ? 'Se descartó 1 cambio'
                : `Se descartaron ${formatoCantidad(descartados)} cambios`,
          });
          despuesDeCambiar();
        } catch (e) {
          toast({
            title: 'No se pudieron descartar los cambios',
            description: mensajeDeError(e, 'Probá de nuevo.'),
            variant: 'destructive',
          });
        }
      },
    });
  };

  /** Una regla vencida vuelve a correr: se le quita la fecha de fin (`validUntil: null`). */
  const onQuitarFin = async (r: PricingRule) => {
    try {
      const actualizada = await pricingRulesApi.update(r.id, { validUntil: null });
      setReglas((rs) =>
        rs.map((x) => (x.id === r.id ? { ...x, ...actualizada, validUntil: null } : x))
      );
      // Lo que dice el aviso sale de cómo queda la regla sin la fecha, no sólo de si
      // está encendida: una vencida con requisitos viejos o con una acción retirada
      // sigue sin correr, y la fila de abajo lo dice (revisión final, item 5).
      const sinFin = { ...r, validUntil: null };
      const e = estadoDeRegla(sinFin);
      toast({
        // Spec 294 (D-R4-4): queda como cambio sin aplicar.
        title: `«${r.name}» queda sin fecha de fin al aplicar`,
        // Apagada: sólo se invita a encenderla a quien puede (re-auditoría 26-sep).
        description: e.corre
          ? 'Rige cuando apliques las reglas y recalcules.'
          : e.porque === 'APAGADA'
            ? permisos.encender
              ? 'Sigue apagada: encendela para que corra.'
              : 'Sigue apagada: la enciende quien tenga permiso para encender reglas.'
            : (avisoDeRegla(sinFin) ?? 'Todavía no corre.'),
      });
      despuesDeCambiar(r.id);
    } catch (e) {
      toast({
        title: 'No se pudo quitar la fecha de fin',
        description: mensajeDeError(e, 'Probá de nuevo.'),
        variant: 'destructive',
      });
    }
  };

  const abrirNueva = (b: BorradorInicial | null = null) => {
    setEditando(null);
    setBorradorInicial(b);
    setAbierto(true);
  };

  const visibles = filtrarPorAlcance(reglas, filtro);
  const reglasDeHoy = useMemo(
    () =>
      reglas
        .filter((r) => r.borrador?.tipo !== 'NUEVA')
        .map((r) => (r.vigente ? { ...r, ...r.vigente } : r)),
    [reglas]
  );

  // Los filtros en píldoras con su número (maqueta Compite). Mientras carga o si
  // falló, sin número: no se sabe cuántas hay (no son 0).
  const filtros = (
    <Pildoras
      aria-label="A quién alcanzan"
      valor={filtro}
      onCambiar={setFiltro}
      opciones={FILTROS.map((f) => ({
        valor: f.clave,
        texto: f.texto,
        cuenta: cargando || error ? null : filtrarPorAlcance(reglas, f.clave).length,
      }))}
    />
  );

  // Lote C1 (D11): aplicar las reglas es ponerlas a correr. Ronda 2 C1: el botón
  // se ve siempre (Reglas ya exige ver reglas, y la vista previa es de lectura);
  // quién manda lo frena la ventana con `puedeMandar`: sin encender, se mira sin
  // «Enviar». Compite (rediseño del 14-oct): con cambios sin aplicar se destaca y
  // dice cuántos; el número va aparte del nombre del botón (lo dice la descripción).
  const hayCambiosSinAplicar = cuantosCambios > 0;
  const idCambios = useId();
  const botonAplicar = (
    <>
      <Button
        variant="outline"
        onClick={() => setRecalculando(true)}
        aria-describedby={hayCambiosSinAplicar ? idCambios : undefined}
        {...sinCambios}
        className={cn(
          'h-9 gap-2 rounded-full px-4',
          hayCambiosSinAplicar &&
            'border-alerta-borde bg-alerta-suave font-semibold text-foreground shadow-sm hover:bg-alerta-suave hover:text-foreground hover:brightness-95'
        )}
      >
        <RefreshCw
          className={cn('h-3.5 w-3.5', hayCambiosSinAplicar && 'text-alerta')}
          aria-hidden="true"
        />
        Aplicar las reglas y recalcular
        {hayCambiosSinAplicar && (
          <span
            aria-hidden="true"
            className="tabular-nums min-w-[1.25rem] rounded-full bg-alerta px-1.5 py-0.5 text-center text-xs font-semibold leading-none text-background"
          >
            {formatoCantidad(cuantosCambios)}
          </span>
        )}
      </Button>
      {hayCambiosSinAplicar && (
        <span id={idCambios} className="sr-only">
          {cuantosCambios === 1
            ? 'Hay 1 cambio sin aplicar.'
            : `Hay ${formatoCantidad(cuantosCambios)} cambios sin aplicar.`}
        </span>
      )}
    </>
  );

  const cuerpoDeReglas = cargando ? (
    <div data-testid="cargando-reglas" className="space-y-2 border-t border-border px-5 py-4">
      <Skeleton className="h-12 w-full rounded-xl" />
      <Skeleton className="h-12 w-full rounded-xl" />
      <Skeleton className="h-12 w-full rounded-xl" />
    </div>
  ) : error ? (
    <div className="border-t border-border px-5 py-4">
      <EstadoError onReintentar={() => void cargar()} className="rounded-xl">
        {error}
      </EstadoError>
    </div>
  ) : reglas.length === 0 ? (
    <div className="border-t border-border px-5 py-5">
      <EstadoVacio titulo="Todavía no hay reglas" icono={ShieldCheck} className="rounded-tarjeta">
        Sin reglas, cada estrategia pone el precio que calcula.
      </EstadoVacio>
    </div>
  ) : visibles.length === 0 ? (
    <div className="border-t border-border px-5 py-5">
      <EstadoVacio
        titulo="Ninguna regla con este filtro."
        className="rounded-tarjeta"
        accion={
          <Button variant="link" size="sm" onClick={() => setFiltro('TODAS')}>
            Ver todas
          </Button>
        }
      />
    </div>
  ) : (
    <ListaDeReglas
      reglas={visibles}
      todas={reglas}
      conteos={conteos}
      onHistorial={setHistorialDe}
      {...(permisos.encender && { onToggle: (r: PricingRule) => void onToggle(r) })}
      {...(permisos.editar && {
        onEditar: (r: PricingRule) => {
          setEditando(r);
          setBorradorInicial(null);
          setAbierto(true);
        },
        onQuitarFin: (r: PricingRule) => void onQuitarFin(r),
      })}
      {...(permisos.borrar && { onBorrar, puedeBorrar })}
      {...((permisos.encender || permisos.editar) && {
        onDescartarCambio,
        puedeDescartarElCambio,
      })}
    />
  );

  // «Nueva regla», el botón principal (uno por pantalla). En Compite va al
  // encabezado de la página, como en la maqueta; suelta, dentro de la tarjeta.
  const botonNueva = permisos.crear ? (
    <Button
      onClick={() => abrirNueva()}
      {...sinCambios}
      className={cn(
        'gap-1.5 rounded-full',
        enCompite ? cn('h-11 px-6 text-base', BOTON_DEGRADADO) : 'h-10 px-5'
      )}
    >
      <Plus className="h-4 w-4" aria-hidden="true" />
      Nueva regla
    </Button>
  ) : null;

  // La tarjeta de la maqueta: arriba los filtros y «Aplicar las reglas y
  // recalcular»; abajo, las reglas agrupadas por tipo.
  const tarjetaDeReglas = (
    <Tarjeta className="overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 px-5 py-3.5">
        {filtros}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {botonAplicar}
          {!enCompite && botonNueva}
        </div>
      </div>
      {cuerpoDeReglas}
    </Tarjeta>
  );

  const contenido = (
    // El `main` de AppLayout no tiene margen: cada pantalla pone el suyo (p-6). Embebida,
    // lo pone el destino que la contiene (el título y la línea de ayuda, también).
    <div className={embebida ? 'space-y-5' : 'space-y-5 p-6'}>
      {!embebida && (
        <div>
          <h1 className="tracking-tight text-2xl font-semibold text-foreground">Reglas</h1>
          <p className="text-sm text-muted-foreground">
            Los límites que ningún precio puede pasar, lo proponga la estrategia que lo proponga
          </p>
        </div>
      )}
      {/* Compite: la línea de ayuda va primero, pegada a las pestañas (la maqueta). */}
      {enCompite && <AyudaReglas compacta />}
      {enCompite && botonNueva && destinoDeLaAccion && createPortal(botonNueva, destinoDeLaAccion)}
      {soloLectura && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          No tenés permiso para cambiar reglas.
        </p>
      )}
      {deAntes.length > 0 && (
        <Aviso
          tono="alerta"
          titulo="Tenés cambios sin aplicar de antes"
          data-testid="cambios-de-antes"
          className="rounded-tarjeta px-4 py-3.5 text-sm"
        >
          <div className="flex flex-wrap gap-2 pt-1">
            {permisos.encender && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 rounded-full"
                onClick={() => setRecalculando(true)}
                {...sinCambios}
              >
                Aplicar…
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="h-8 rounded-full"
              onClick={() => void onDescartarLosDeAntes()}
              {...sinCambios}
            >
              Descartarlos
            </Button>
          </div>
        </Aviso>
      )}
      <AvisoReglasSinRecalcular
        reglas={sinRecalcular}
        cambios={cambios}
        accion={
          // Spec 294 (D-R4-4): con cambios sin aplicar, aplicarlos y descartarlos
          // es de quien puede encender; sin ese permiso, se dice quién.
          cambios ? (
            permisos.encender ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 rounded-full"
                  onClick={() => setRecalculando(true)}
                  {...sinCambios}
                >
                  Aplicar las reglas y recalcular
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 rounded-full"
                  onClick={onDescartarTodos}
                  {...sinCambios}
                >
                  Descartar todos los cambios
                </Button>
              </div>
            ) : (
              <span className="text-muted-foreground">Los aplica quien puede encender reglas.</span>
            )
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="h-8 rounded-full"
              onClick={() => setRecalculando(true)}
              {...sinCambios}
            >
              Aplicar las reglas y recalcular
            </Button>
          )
        }
      />
      {!enCompite && <AyudaReglas />}
      {embebida ? (
        tarjetaDeReglas
      ) : (
        <Tabs value={pestana} onValueChange={(v) => setPestana(v as 'reglas' | 'productos')}>
          <TabsList>
            <TabsTrigger value="reglas">Reglas</TabsTrigger>
            <TabsTrigger value="productos">Cómo está cada producto</TabsTrigger>
          </TabsList>
          <TabsContent value="reglas" className="space-y-3">
            {tarjetaDeReglas}
          </TabsContent>
          <TabsContent value="productos">
            {/* `key`: al terminar un recálculo la foto se vuelve a leer; la pestaña
                no se desmonta sola. «¿Y si…?» ya no se monta acá (agregados del
                29-sep, A5). Spec 294: la foto es de lo que rige hoy, así que sus
                reglas también (sin las nuevas sin aplicar, con lo vigente). */}
            <ComoEstaCadaProducto
              key={versionFoto}
              reglas={reglasDeHoy}
              filtroInicial={inicial.filtro}
            />
          </TabsContent>
        </Tabs>
      )}
      <NuevaReglaDialog
        abierto={abierto}
        regla={editando}
        inicial={borradorInicial}
        reglas={reglas}
        conteos={conteos}
        // Crear encendida pide encender/apagar (Task 0, ajuste 5).
        puedeEncender={permisos.encender}
        onCerrar={() => setAbierto(false)}
        onGuardada={(r) => {
          setAbierto(false);
          despuesDeCambiar(r.id);
        }}
      />
      <RecalcularDialog
        abierto={recalculando}
        puedeMandar={permisos.encender}
        onCerrar={() => setRecalculando(false)}
        onAplicado={() => {
          // Ronda de la T11 (I1): con el 202 los cambios ya rigen (se promovieron):
          // la lista y el aviso se releen ya, sin esperar al recálculo; lo tocado
          // en esta visita ya no es un cambio sin aplicar.
          setTocadas(new Set());
          leerAviso();
          void cargar(true);
        }}
        onTermino={() => {
          // Spec 294: al terminar, lo aplicado ya rige: la lista, el aviso y la foto.
          leerAviso();
          void cargar(true);
          setVersionFoto((v) => v + 1);
        }}
        onIrAPorDecidir={() => {
          // Ronda final C1: Reglas sólo se usa embebida y quien la embeja pasa
          // siempre a dónde ir, con el filtro de «Origen: recálculo». La caída a
          // «?destino=bandeja» (sin filtro) era de la pantalla suelta, ya fuera
          // de uso.
          onIrAPorDecidirDelRecalculo?.();
        }}
      />
      <Dialog open={historialDe != null} onOpenChange={(o) => !o && setHistorialDe(null)}>
        <DialogContent className="max-w-2xl" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="min-w-0 pr-6 [overflow-wrap:anywhere]">
              Historial de «{historialDe?.name}»
            </DialogTitle>
          </DialogHeader>
          {historialDe && (
            <HistorialDeCambios
              pedir={(antesDe) => pricingRulesApi.historial(historialDe.id, antesDe)}
              enPalabras={historialDeReglaEnPalabras}
            />
          )}
        </DialogContent>
      </Dialog>
      <ConfirmDialog />
    </div>
  );

  return embebida ? contenido : <AppLayout>{contenido}</AppLayout>;
}
